import { createFileRoute, Link } from "@tanstack/react-router";
import { Gamepad2, Globe, Loader2, Lock, Shield, Swords, UserPlus } from "lucide-react";
import { useMemo } from "react";
import { toast } from "sonner";

import { ClubHubCard } from "../../../../components/club/ClubHubCard";
import { ClubStatsCard } from "../../../../components/club/ClubStatsCard";
import type { QueueUserStats } from "../../../../components/club/QueueCard";
import { QueueCard } from "../../../../components/club/QueueCard";
import { DiscordIcon, XIcon } from "../../../../components/icons/BrandIcons";
import { Badge, Button } from "../../../../components/ui";
import { useSession } from "../../../../lib/auth-client";
import { trpc } from "../../../../lib/trpc";

export const Route = createFileRoute("/app/clubs/$slug/")({
  component: ClubDetailPage,
});

// ---------------------------------------------------------------------------
// Privacy config
// ---------------------------------------------------------------------------

const privacyConfig = {
  public: { icon: Globe, label: "Public", color: "green" as const },
  private: { icon: Lock, label: "Private", color: "yellow" as const },
  invite_only: { icon: Shield, label: "Invite Only", color: "purple" as const },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function ClubDetailPage() {
  const { slug } = Route.useParams();
  const { data: club, isLoading } = trpc.club.get.useQuery({ slug });
  const { data: myClubs } = trpc.club.getMyClubs.useQuery();
  const membership = myClubs?.find((c) => c.slug === slug);
  const isMember = !!membership;

  if (isLoading) return <ClubPageSkeleton />;

  if (!club) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
        <div className="relative mb-8">
          <div className="absolute -inset-6 rounded-full bg-red-500/10 blur-3xl" />
          <div className="relative flex items-center justify-center w-20 h-20 rounded-2xl bg-surface-800/60 border border-surface-700/40 backdrop-blur-sm">
            <Shield className="size-9 text-surface-500" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-white mb-2 tracking-tight">Club not found</h2>
        <p className="text-sm text-surface-400 mb-8 max-w-xs leading-relaxed">
          This club doesn&apos;t exist or may have been removed.
        </p>
        <Link to="/app/clubs">
          <Button variant="outline">Browse Clubs</Button>
        </Link>
      </div>
    );
  }

  const privacy = privacyConfig[club.privacy];
  const PrivacyIcon = privacy.icon;
  const isPublic = club.privacy === "public";
  const isPrivate = club.privacy === "private";
  const isInviteOnly = club.privacy === "invite_only";

  const showLockedView = isPrivate && !isMember;

  return (
    <div className="relative max-w-[1400px] mx-auto pb-8">
      {/* ── Page atmosphere (mobile only — sidebar provides visual interest on desktop) ── */}
      <div className="lg:hidden absolute inset-0 bg-[radial-gradient(ellipse_70%_50%_at_50%_0%,_rgba(38,217,181,0.06)_0%,_transparent_70%)] pointer-events-none" />

      {/* ── Banner + Club Identity (mobile only — desktop uses sidebar) ── */}
      <div className="hidden relative overflow-hidden mt-2 mx-2 sm:mt-4 sm:mx-4 rounded-xl border border-surface-800 shadow-[0_0_50px_-12px_rgba(38,217,181,0.1)] bg-surface-900">
        {/* Banner background */}
        <div className="relative hidden sm:block sm:h-40">
          {club.bannerUrl ? (
            <img src={club.bannerUrl} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-surface-900" />
              <div className="absolute inset-0 bg-brand-500/[0.07]" />
              <div className="absolute inset-0 opacity-[0.06] bg-chevron-brand" />
            </div>
          )}
          {/* Bottom fade */}
          <div className="absolute inset-0 bg-gradient-to-t from-surface-900 via-transparent to-transparent" />
          <div className="absolute inset-0 noise-overlay" />

          {/* Privacy badge */}
          <div className="absolute top-4 right-4 z-10">
            <Badge
              variant="subtle"
              color={privacy.color}
              size="sm"
              className="backdrop-blur-md bg-surface-900/70 border border-surface-800/40"
            >
              <PrivacyIcon className="size-3 mr-1" />
              {privacy.label}
            </Badge>
          </div>
        </div>

        {/* Club identity — positioned over the banner bottom */}
        <div className="relative px-6 pt-4 sm:pt-0 sm:-mt-16 pb-4 z-10">
          <div className="flex flex-col sm:flex-row sm:items-end gap-4">
            {/* Avatar */}
            <div className="relative shrink-0">
              {club.avatarUrl ? (
                <img
                  src={club.avatarUrl}
                  alt={club.name}
                  className="relative w-20 h-20 rounded-2xl bg-surface-700 object-cover ring-[3px] ring-surface-900 shadow-xl shadow-black/40"
                />
              ) : (
                <div className="relative w-20 h-20 rounded-2xl bg-surface-800 border border-surface-800/60 flex items-center justify-center text-white font-bold text-3xl shadow-xl shadow-black/40">
                  {club.name.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Name + description + socials */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white truncate tracking-tight">
                {club.name}
              </h1>
              {!showLockedView && club.description && (
                <p className="text-sm text-surface-400 mt-1 line-clamp-2 max-w-lg">
                  {club.description}
                </p>
              )}

              {/* Social links inline */}
              {!showLockedView && (club.discordUrl || club.twitterUrl || club.websiteUrl) && (
                <div className="flex items-center gap-2 mt-2.5">
                  {club.discordUrl && (
                    <a
                      href={club.discordUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-7 h-7 rounded-lg bg-surface-800/80 border border-surface-800/40 text-surface-400 hover:text-[#5865F2] hover:border-[#5865F2]/30 hover:bg-[#5865F2]/10 transition-all"
                      title="Discord"
                    >
                      <DiscordIcon className="size-3.5" />
                    </a>
                  )}
                  {club.twitterUrl && (
                    <a
                      href={club.twitterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-7 h-7 rounded-lg bg-surface-800/80 border border-surface-800/40 text-surface-400 hover:text-white hover:border-surface-600 hover:bg-surface-700/50 transition-all"
                      title="Twitter"
                    >
                      <XIcon className="size-3" />
                    </a>
                  )}
                  {club.websiteUrl && (
                    <a
                      href={club.websiteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-7 h-7 rounded-lg bg-surface-800/80 border border-surface-800/40 text-surface-400 hover:text-brand-400 hover:border-brand-400/30 hover:bg-brand-400/10 transition-all"
                      title="Website"
                    >
                      <Globe className="size-3" />
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* Join button (non-members, mobile only — desktop uses sidebar) */}
            {!isMember && (
              <div className="shrink-0 flex items-center gap-2">
                {isPublic ? (
                  <MobileJoinButton clubId={club.id} slug={slug} />
                ) : (
                  <Button
                    variant="outline"
                    size="md"
                    disabled
                    leftIcon={<Lock className="size-4" />}
                  >
                    {isInviteOnly ? "Invite Only" : "Private"}
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────── */}
      <div className="px-3 sm:px-6 mt-6 sm:mt-8">
        {showLockedView ? (
          <>
            <PrivateClubLocked />
            <ClubGamesOverview clubId={club.id} />
          </>
        ) : (
          <MemberDashboard
            clubId={club.id}
            slug={slug}
            role={membership?.role}
            isMember={isMember}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Member Dashboard
// ---------------------------------------------------------------------------

function MemberDashboard({
  clubId,
  slug,
  role,
  isMember = true,
}: {
  clubId: string;
  slug: string;
  role?: string;
  isMember?: boolean;
}) {
  return (
    <div className="grid grid-cols-1 min-[1150px]:grid-cols-2 gap-5 items-start">
      {/* Left column — stats & queues */}
      <div className="space-y-5 order-2 min-[1150px]:order-1">
        {isMember && <ClubStatsCard clubId={clubId} slug={slug} />}
        <DashboardQueues clubId={clubId} slug={slug} isMember={isMember} />
      </div>

      {/* Right column — hub (member-only: getActivity requires membership) */}
      {isMember && (
        <div className="order-1 min-[1150px]:order-2">
          <ClubHubCard clubId={clubId} role={role} />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard: Active Queues
// ---------------------------------------------------------------------------

function DashboardQueues({
  clubId,
  slug,
  isMember = true,
}: {
  clubId: string;
  slug: string;
  isMember?: boolean;
}) {
  const { data: queues, isLoading } = trpc.queue.list.useQuery({ clubId });
  const { data: games } = trpc.clubGame.list.useQuery({ clubId });

  const gameNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (games) for (const g of games) map.set(g.id, g.gameName ?? g.name);
    return map;
  }, [games]);

  const { data: session } = useSession();
  const { data: club } = trpc.club.get.useQuery({ clubId });
  const placementMatchCount = club?.placementMatchCount ?? 10;

  const { data: myStats } = trpc.clubMember.getStats.useQuery(
    { clubId, userId: session?.user?.id ?? "" },
    { enabled: isMember && !!session?.user?.id },
  );

  const statsByQueue = useMemo(() => {
    const map = new Map<string, QueueUserStats>();
    for (const s of myStats ?? []) {
      map.set(s.queueId, {
        displayElo: s.displayElo,
        isPlacement: s.isPlacement,
        matchesPlayed: s.matchesPlayed,
        wins: s.wins,
        losses: s.losses,
        rank: s.rank,
        division: s.division,
        position: s.position,
      });
    }
    return map;
  }, [myStats]);

  const sortedQueues = useMemo(
    () =>
      [...(queues ?? [])]
        .sort((a, b) => {
          const aPlayed = statsByQueue.get(a.id)?.matchesPlayed ?? 0;
          const bPlayed = statsByQueue.get(b.id)?.matchesPlayed ?? 0;
          return bPlayed - aPlayed;
        })
        .slice(0, 6),
    [queues, statsByQueue],
  );

  const queueIds = useMemo(() => sortedQueues.map((q) => q.id), [sortedQueues]);

  const { data: batchStatus } = trpc.queueEntry.batchStatus.useQuery(
    { queueIds },
    { enabled: isMember && queueIds.length > 0 },
  );

  if (isLoading) {
    return (
      <div className="relative rounded-xl border border-surface-800/40 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-surface-800/80 via-surface-850 to-surface-800/60" />
        <div className="relative">
          <div className="px-4 py-3 flex items-center gap-2.5">
            <Swords className="size-4 text-brand-400" />
            <span className="text-sm font-display font-bold text-white">Active Queues</span>
          </div>
          <div className="border-t border-separator p-4 space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-32 rounded-xl bg-surface-800/30 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      <div className="relative">
        {/* Header */}
        <div className="px-4 py-3 flex items-center gap-2.5">
          <Swords className="size-4 text-brand-400" />
          <span className="text-sm font-display font-bold text-white">Active Queues</span>
          {sortedQueues.length > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-brand-500/10 border border-brand-500/20 text-2xs font-bold text-brand-400 tabular-nums">
              {queues?.length ?? 0}
            </span>
          )}
        </div>

        {sortedQueues.length === 0 ? (
          <div className="border-t border-separator px-4 py-12 flex flex-col items-center text-center">
            <Swords className="size-6 text-surface-600 mb-2" />
            <p className="text-sm font-medium text-surface-300">No queues yet</p>
            <p className="text-xs text-surface-500 mt-1">Queues will appear here once created</p>
          </div>
        ) : (
          <div className="border-t border-separator p-4 space-y-4">
            {sortedQueues.map((queue) => (
              <QueueCard
                key={queue.id}
                queue={queue}
                clubGameId={queue.clubGameId}
                gameName={gameNameMap.get(queue.clubGameId) ?? ""}
                userStats={isMember ? statsByQueue.get(queue.id) : undefined}
                placementMatchCount={placementMatchCount}
                slug={slug}
                queueStatus={isMember ? batchStatus?.[queue.id] : undefined}
                viewOnly={!isMember}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Club Games Overview (public / private view)
// ---------------------------------------------------------------------------

function ClubGamesOverview({ clubId }: { clubId: string }) {
  const { data: games, isLoading } = trpc.clubGame.list.useQuery({ clubId });

  if (isLoading) {
    return (
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Gamepad2 className="size-4 text-brand-400" />
          <span className="text-xs font-bold text-white uppercase tracking-wider">Games</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="h-10 w-32 rounded-lg bg-surface-700/20 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!games || games.length === 0) return null;

  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Gamepad2 className="size-4 text-brand-400" />
        <span className="text-xs font-bold text-white uppercase tracking-wider">Games</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {games.map((game) => (
          <div
            key={game.id}
            className="flex items-center gap-2.5 px-3.5 py-2 rounded-lg border border-surface-800/40 bg-surface-800/50"
          >
            <span className="text-sm font-medium text-surface-200">{game.name}</span>
            {game.teamSize && (
              <span className="text-xs text-surface-500 tabular-nums">
                {game.teamSize}v{game.teamSize}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mobile Join Button
// ---------------------------------------------------------------------------

function MobileJoinButton({ clubId, slug }: { clubId: string; slug: string }) {
  const utils = trpc.useUtils();
  const joinMutation = trpc.clubMember.join.useMutation({
    onSuccess: () => {
      toast.success("Joined club!");
      void utils.club.getMyClubs.invalidate();
      void utils.club.get.invalidate({ slug });
    },
    onError: (err) => {
      if (err.data?.code === "CONFLICT") {
        toast.error("You are already a member of this club");
      } else if (err.data?.code === "FORBIDDEN") {
        toast.error("This club is not accepting new members");
      } else {
        toast.error("Failed to join club");
      }
    },
  });

  return (
    <Button
      variant="primary"
      size="md"
      leftIcon={
        joinMutation.isPending ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <UserPlus className="size-4" />
        )
      }
      disabled={joinMutation.isPending}
      onClick={() => joinMutation.mutate({ clubId })}
    >
      {joinMutation.isPending ? "Joining..." : "Join Club"}
    </Button>
  );
}

// ---------------------------------------------------------------------------
// Private Club Locked View
// ---------------------------------------------------------------------------

function PrivateClubLocked() {
  return (
    <div className="relative flex flex-col items-center justify-center py-20 rounded-xl overflow-hidden text-center">
      <div className="absolute inset-0 bg-surface-800/30 border border-surface-800 rounded-xl" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(234,179,8,0.06)_0%,_transparent_60%)]" />

      <div className="relative mb-5">
        <div className="absolute inset-0 rounded-full bg-yellow-500/15 blur-2xl scale-[2]" />
        <div className="relative flex items-center justify-center w-18 h-18 rounded-2xl bg-surface-800/80 border border-yellow-500/20">
          <Lock className="size-9 text-yellow-400" />
        </div>
      </div>
      <h3 className="relative text-xl font-bold text-white mb-2">This club is private</h3>
      <p className="relative text-base text-surface-400 max-w-xs">
        You need an invite link from a club member to join this club.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Club Page Skeleton (shown during initial load)
// ---------------------------------------------------------------------------

function ClubPageSkeleton() {
  return (
    <div className="relative max-w-4xl mx-auto pb-8 px-3 sm:px-6 mt-6 sm:mt-8">
      <div className="space-y-6">
        {/* Stats card skeleton */}
        <div className="relative rounded-xl border border-surface-800/40 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-surface-800/80 via-surface-850 to-surface-800/60" />
          <div className="relative p-5 space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <div className="h-5 w-32 rounded bg-surface-700/30 animate-pulse" />
                <div className="h-10 w-28 rounded bg-surface-700/40 animate-pulse" />
              </div>
              <div className="w-16 h-16 rounded-full bg-surface-700/30 animate-pulse" />
            </div>
            <div className="grid grid-cols-3 gap-4 pt-4 border-t border-separator">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex flex-col items-center gap-1.5">
                  <div className="h-2.5 w-12 rounded bg-surface-700/20 animate-pulse" />
                  <div className="h-5 w-10 rounded bg-surface-700/30 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Queues skeleton */}
        <div className="space-y-4">
          <div className="flex items-center gap-2.5">
            <div className="h-4 w-4 rounded bg-surface-700/30 animate-pulse" />
            <div className="h-4 w-24 rounded bg-surface-700/30 animate-pulse" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-32 rounded-xl bg-surface-800/30 animate-pulse" />
            ))}
          </div>
        </div>

        {/* Two-column skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-6">
          <div className="rounded-xl border border-surface-800/40 overflow-hidden">
            <div className="bg-gradient-to-b from-surface-800/80 via-surface-850 to-surface-800/60">
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-separator">
                <div className="h-4 w-4 rounded bg-surface-700/30 animate-pulse" />
                <div className="h-4 w-16 rounded bg-surface-700/30 animate-pulse" />
              </div>
              <div className="divide-y divide-separator">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center gap-3 px-5 py-3.5">
                    <div className="w-1 h-8 rounded-full bg-surface-700/30 animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3.5 w-36 rounded bg-surface-700/30 animate-pulse" />
                      <div className="h-2.5 w-28 rounded bg-surface-700/20 animate-pulse" />
                    </div>
                    <div className="h-5 w-16 rounded bg-surface-700/20 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-surface-800/40 h-64 bg-surface-800/30 animate-pulse" />
        </div>
      </div>
    </div>
  );
}
